// Everything outside React that has to follow the language actually rendered.
//
// `<html lang>` is the reason this module exists: assistive technology reads it
// to choose a voice and a pronunciation dictionary, and `index.html` can only
// state the language of a page nobody has chosen yet. ACCS-01 / WCAG 2.2
// SC 3.1.1 asks for the declaration to be true, which means it has to move when
// the reader moves it — not be set once at build time.
//
// dayjs and `@simsm/ui-shared`'s date formatter ride along because their locale
// is the same kind of fact: one global setting, correct only while it matches
// what the interface is speaking. The formatter cannot read the language for
// itself — ui-shared carries no translation runtime by design (see the note on
// ToastHost in app/providers.tsx) — so this is the only thing keeping every
// date in the product in the language on screen.

import dayjs from 'dayjs';
import 'dayjs/locale/ro';
import 'dayjs/locale/ru';
import type { TolgeeInstance } from '@tolgee/web';
import { setDateLocale } from '@simsm/ui-shared';
import { DEFAULT_LOCALE, isSupportedLocale, readStoredLocale } from '@simsm/ui-i18n';

/** Declares, on the document, the language the interface is rendering in. */
export function applyDocumentLanguage(language: string | undefined): void {
  const locale = isSupportedLocale(language) ? language : DEFAULT_LOCALE;
  document.documentElement.lang = locale;
  // 'en' needs no import — dayjs bundles it as its built-in locale.
  dayjs.locale(locale);
  setDateLocale(locale);
}

/**
 * Binds the declaration to the instance for the life of the document. Tolgee
 * resolves its initial language inside `run()`, which happens after this
 * returns, so the stored preference seeds the first declaration and the
 * `language` event carries every one after it. Nothing unbinds: the
 * subscription is as long-lived as the page it describes.
 */
export function bindLanguageSideEffects(tolgee: TolgeeInstance): void {
  applyDocumentLanguage(tolgee.getLanguage() ?? readStoredLocale());
  tolgee.on('language', (event) => applyDocumentLanguage(event.value));
}
